import React, { useState } from "react";
import { Box, Grid2, Typography } from "@mui/material";
import { ThemeProperties } from "../../Theme/ThemeProperties";
import { decryptTheParams, encryptTheParams } from "../../Helper/QueryParams/EncryptDecrypt";
import { useNavigate } from "react-router-dom";
import TabWithColorDigitalTwins from "../../Features/Tabs/TabWithColorDigitalTwins";
import AppCache from "../../Helper/Common/AppCache";
import useCustomDashboardGlobalFilter from "../../Hooks/useCustomDashboardGlobalFilter";
import GlobalFilter from "../Overview/components/GlobalFilter";
import FilterBar from "../../Components/FilterBar";
import MultiSelectDropdown from "../Overview/components/MultiSelectDropdown";
import StatsSection from "./StatsSection";
import IssueStreamSection from "./IssueStreamSection";

/**
 * CustomOverviewMain — orchestrator for the Custom Observability Platform overview page.
 *
 * Reads `customDashboards[activeKey].overview` from AppCache to determine which widgets
 * to show and how each widget is configured. Widget rendering is driven by the
 * `widgets` array; individual widget configs (fleetMetrics, etc.) are passed down
 * as props to the relevant section components.
 *
 * Global filter is only shown when customObservabilityPlatform defines its own
 * globalFilter — never falls back to the root org globalFilter.
 */
const CustomOverviewMain = () => {
    const navigate = useNavigate();
    const queryParams = decryptTheParams();
    const [tabValue, setTabValue] = useState(0);

    // Read custom platform config once at mount.
    // AppCache.getItem() calls JSON.parse() on every read — plain const reads
    // produce new array/object references on every render, which would cause
    // child components that depend on these as props to re-render unnecessarily.
    const [widgets] = useState(() => {
        const cfg = AppCache.getItem("customObservabilityConfig") || {};
        return cfg.overview?.widgets || [];
    });
    const [metricsConfig] = useState(() => {
        const cfg = AppCache.getItem("customObservabilityConfig") || {};
        return cfg.overview?.fleetMetrics || {};
    });
    const [liveIssueStreamConfig] = useState(() => {
        const cfg = AppCache.getItem("customObservabilityConfig") || {};
        return cfg.overview?.liveIssueStream || {};
    });

    const {
        globalFilters,
        setGlobalFilters,
        globalFilterConfig,
        allFleets,
        allOnboardingLocations,
        allOnboardingLocationsHierarchy,
        canUseGlobalAdvancedFilter,
        handleFleetAndLocationChange,
        handleDepotSelection,
    } = useCustomDashboardGlobalFilter();

    const tabs = [{ id: 0, name: "Overview", color: ThemeProperties.purple }];

    const changeTab = (newValue) => {
        setTabValue(newValue);
        encryptTheParams({ ...queryParams, tabValue: newValue }, navigate);
    };

    return (
        <>
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0, borderBottom: "1px solid #E4E3E3" }}>
                <TabWithColorDigitalTwins ChangeTheTab={changeTab} tabs={tabs} />
            </Box>

            <Box sx={{ px: 3, py: 2, mb: 2 }}>
                <Grid2 container gap={2} flexDirection="column">
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography variant="fw400s14c494949" sx={{ fontSize: "18px", lineHeight: "24px" }}>
                            Here&apos;s What&apos;s Happening in Your Fleet Today
                        </Typography>
                        <FilterBar
                          testId="custom-overview-filters"
                          filters={[
                            {
                              key: "customerLocation",
                              primary: true,
                              node: (
                                <>
                            {canUseGlobalAdvancedFilter && globalFilterConfig ? (
                                <GlobalFilter
                                    allFleets={allFleets}
                                    allOnboardingLocationsHierarchy={allOnboardingLocationsHierarchy}
                                    selectedFleetIds={globalFilters?.fleets || []}
                                    selectedLocations={globalFilters?.hierarchicalLocation}
                                    onChange={handleFleetAndLocationChange}
                                />
                            ) : (
                                allOnboardingLocations?.depots?.length > 0 && (
                                    <MultiSelectDropdown
                                        selectedOptions={globalFilters?.onboardingLocations?.depots || []}
                                        options={allOnboardingLocations?.depots || []}
                                        label="Depot"
                                        onChange={handleDepotSelection}
                                    />
                                )
                            )}
                                </>
                              ),
                            },
                          ]}
                        />
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                        <StatsSection
                            globalFilters={globalFilters}
                            widgets={widgets}
                            metricsConfig={metricsConfig}
                        />
                        <IssueStreamSection
                            globalFilters={globalFilters}
                            widgets={widgets}
                            liveIssueStreamConfig={liveIssueStreamConfig}
                        />
                    </Box>
                </Grid2>
            </Box>
        </>
    );
};

export default CustomOverviewMain;
